import { useMemo, useState } from 'react';

import {
    useGetAdminOrganizationsQuery,
    useGetAdminUsersQuery,
} from '../entities/access/api/accessApi';
import { toAccessOrganization, toAccessUser, ROLE_TO_DTO } from '../entities/access/model/mappers';
import type {
    AccountStatusFilter,
    OrganizationFilter,
    OrganizationStatusFilter,
    RoleFilter,
} from '../types/access';
import { NO_ORGANIZATION } from '../types/access';
import { useDebouncedValue } from './useDebouncedValue';
import { DEFAULT_PAGE_SIZE } from '../shared/config/pagination';

const ORGANIZATIONS_FOR_SELECT = 500;

export function useUsersFilters(perPage = DEFAULT_PAGE_SIZE) {
    const [query, setQueryState] = useState('');
    const [role, setRoleState] = useState<RoleFilter>('');
    const [organization, setOrganizationState] = useState<OrganizationFilter>('');
    const [status, setStatusState] = useState<AccountStatusFilter>('');
    const [page, setPage] = useState(1);

    const debouncedQuery = useDebouncedValue(query);

    const { data, isLoading, isFetching, error } = useGetAdminUsersQuery({
        page_num: page,
        page_size: perPage,
        query: debouncedQuery.trim(),
        role: role === '' ? undefined : ROLE_TO_DTO[role],
        organization_id: organization === '' ? undefined : organization,
        status: status === '' ? undefined : status,
    });

    const visible = useMemo(() => (data?.data ?? []).map(toAccessUser), [data]);

    const isFiltered = query.trim() !== '' || role !== '' || organization !== '' || status !== '';

    function reset() {
        setQueryState('');
        setRoleState('');
        setOrganizationState('');
        setStatusState('');
        setPage(1);
    }

    function withReset<T>(setter: (value: T) => void) {
        return (value: T) => {
            setter(value);
            setPage(1);
        };
    }

    return {
        query,
        setQuery: withReset(setQueryState),
        role,
        setRole: withReset(setRoleState),
        organization,
        setOrganization: withReset(setOrganizationState),
        status,
        setStatus: withReset(setStatusState),
        isFiltered,
        reset,
        total: data?.count ?? visible.length,
        visible,
        page,
        setPage,
        pageCount: Math.max(1, data?.pages ?? 1),
        perPage,
        isLoading,
        isFetching,
        error,
    };
}

export function useOrganizationsFilters(perPage = DEFAULT_PAGE_SIZE) {
    const [query, setQueryState] = useState('');
    const [status, setStatusState] = useState<OrganizationStatusFilter>('');
    const [page, setPage] = useState(1);

    const debouncedQuery = useDebouncedValue(query);

    const { data, isLoading, isFetching, error } = useGetAdminOrganizationsQuery({
        page_num: page,
        page_size: perPage,
        query: debouncedQuery.trim(),
        status: status === '' ? undefined : status,
    });

    const visible = useMemo(() => (data?.data ?? []).map(toAccessOrganization), [data]);

    const isFiltered = query.trim() !== '' || status !== '';

    function reset() {
        setQueryState('');
        setStatusState('');
        setPage(1);
    }

    function withReset<T>(setter: (value: T) => void) {
        return (value: T) => {
            setter(value);
            setPage(1);
        };
    }

    return {
        query,
        setQuery: withReset(setQueryState),
        status,
        setStatus: withReset(setStatusState),
        isFiltered,
        reset,
        total: data?.count ?? visible.length,
        visible,
        page,
        setPage,
        pageCount: Math.max(1, data?.pages ?? 1),
        perPage,
        isLoading,
        isFetching,
        error,
    };
}

export function useOrganizationOptions() {
    const { data } = useGetAdminOrganizationsQuery({
        page_num: 1,
        page_size: ORGANIZATIONS_FOR_SELECT,
    });

    return useMemo(() => (data?.data ?? []).map(toAccessOrganization), [data]);
}

export { NO_ORGANIZATION };